import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    SunIcon,
    MoonIcon
} from '@heroicons/react/24/outline'
import icon from '../assets/images/cw.png'
import { useTheme } from '../context/ThemeContext'
import { NAV_LINKS } from '../constants'

const Sidebar = ({ isOpen, onClose, onCollapse }) => {
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { isDark, toggleTheme } = useTheme();


    const handleCollapse = () => {
        setIsCollapsed(!isCollapsed);
        if (onCollapse) onCollapse(!isCollapsed);
    };

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 h-full bg-white dark:bg-crypto-card border-r border-gray-200 dark:border-gray-800 z-50 transition-all duration-300 ease-in-out group/sidebar ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                } lg:translate-x-0 ${isCollapsed ? 'lg:w-20' : 'w-64'}`}
            >
                {/* Logo */}
                <div className="relative flex items-center justify-center p-6 border-b border-gray-200 dark:border-gray-800">
                    <Link to="/" className="flex items-center space-x-3 group">
                        <img
                            src={icon}
                            alt="Crypto"
                            className="w-12 h-12 rounded-full transition-all flex-shrink-0"
                        />
                        {!isCollapsed && (
                            <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400 whitespace-nowrap">Info Crypto</h1>
                        )}
                    </Link>

                    {/* Close button (mobile only) */}
                    <button
                        onClick={onClose}
                        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-crypto-cardHover transition-colors"
                        aria-label="Close navigation menu"
                    >
                        <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                    </button>

                    {/* Collapse button (desktop only) - Floating style */}
                    <button
                        onClick={handleCollapse}
                        className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 transition-all hover:scale-110 opacity-0 group-hover/sidebar:opacity-100"
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isCollapsed ? (
                            <ChevronRightIcon className="w-3.5 h-3.5" />
                        ) : (
                            <ChevronLeftIcon className="w-3.5 h-3.5" />
                        )}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-2">
                    {NAV_LINKS.map(({ path, label, icon: Icon }) => {
                        const isActive = location.pathname === path;

                        return (
                            <div key={path} className="relative group">
                                <Link
                                    to={path}
                                    onClick={onClose}
                                    className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-all ${
                                        isActive
                                            ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/20'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-crypto-cardHover hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                >
                                    <Icon className="w-5 h-5 flex-shrink-0" />
                                    {!isCollapsed && (
                                        <span className="font-medium">{label}</span>
                                    )}
                                </Link>

                                {/* Tooltip for collapsed state */}
                                {isCollapsed && (
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
                                        {label}
                                        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* Theme Toggle */}
                <div className={`absolute ${isCollapsed ? 'bottom-4' : 'bottom-24'} left-0 right-0 px-4 transition-all duration-300`}>
                    <button
                        onClick={toggleTheme}
                        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-all bg-gray-100 dark:bg-crypto-cardHover hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white group`}
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        <div className="relative w-5 h-5">
                            <SunIcon className={`w-5 h-5 absolute transition-all duration-300 ${isDark ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                            <MoonIcon className={`w-5 h-5 absolute transition-all duration-300 ${isDark ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
                        </div>
                        {!isCollapsed && (
                            <span className="font-medium">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                        )}
                    </button>
                </div>

                {/* Footer info */}
                {!isCollapsed && (
                    <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-200 dark:border-gray-800">
                        <div className="text-xs text-gray-700 dark:text-gray-400">
                            <p className="mb-1 font-semibold">© {new Date().getFullYear()} Info Crypto</p>
                            <p>Real-time crypto data</p>
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
};

export default Sidebar;
